import React from 'react';
import PropTypes from 'prop-types';
import EmptyState from 'components/Shared/EmptyState/EmptyState';
import InspectHealthInfoDialog from 'components/Inspect/InspectHealthInfoDialog';
import { SlateDialog } from 'components/Shared/SlateDialog';

// Fallback click handler for KPI cards when no onCardClick prop is supplied
// (e.g. in isolation / tests). Data-backed cards navigate via the onCardClick
// prop; only the Neglected placeholder (shown while the endpoint does not
// deliver the count) remains inert.
function noop() {}

// Arc uses a 270-degree sweep (3/4 circle). r=48, circumference 2*PI*48 = 301.6;
// the 270deg portion is 301.6 * 0.75 = 226.2.
function computeArcDasharray(score) {
  var maxArc = 226.2;
  var totalCirc = 301.6;
  var filled = maxArc * (score / 100);
  var gap = totalCirc - filled;
  return filled.toFixed(1) + ' ' + gap.toFixed(1);
}

// Bar color for a per-inspection pass rate percentage, using the same
// thresholds as the health grade (>= 75 good, >= 50 ok, else bad).
function getBarColor(passRate) {
  if (passRate >= 75) return 'var(--perf-health-good)';
  if (passRate >= 50) return 'var(--perf-health-ok)';
  return 'var(--perf-health-bad)';
}

export var NEGLECT_POINTS_PER_ASSET = 3;
export var NEGLECT_PENALTY_CAP = 15;

// Weighted composite health score. Three ratio factors (each 0-100) blend by
// weight; a factor whose denominator is zero has no data, drops out, and the
// remaining weights renormalize. A capped penalty for neglected assets is
// then subtracted. `neglected` is nullable in the store: null means the
// endpoint does not deliver the count yet (penalty inert), 0 means a real
// zero. Exported for unit testing and for the explainer dialog.
//
// Health is a "now" score: the API reports a failed inspection whose
// deficiency has been closed out as passed (and counts it in `corrected`),
// so only open deficiencies lower the pass rate and closing one raises it.
export function computeHealthView(performance) {
  var passed = (performance && performance.passed) || 0;
  var failed = (performance && performance.failed) || 0;
  var total = (performance && performance.total) || 0;
  var overdue = (performance && performance.overdue) || 0;
  var upcoming = (performance && performance.upcoming) || 0;
  // Lifecycle-completed count (spec section 7): __inspect_state == completed,
  // or the legacy shape. NOT passed + failed -- an entry amended to Incomplete
  // after completion still counts here (it stays completed for scheduling),
  // even though it drops out of the graded passRate factor below.
  var completed = (performance && performance.completed) || 0;
  var neglectedRaw = performance ? performance.neglected : null;
  var neglectAvailable = neglectedRaw !== null && neglectedRaw !== undefined;
  var neglectCount = neglectAvailable ? neglectedRaw : 0;

  var graded = passed + failed;
  var scheduled = upcoming + overdue;

  var factors = [
    {
      key: 'passRate',
      weight: 0.5,
      present: graded > 0,
      value: graded > 0 ? (passed / graded) * 100 : null,
      effectiveWeight: null,
    },
    {
      key: 'completion',
      weight: 0.2,
      present: total > 0,
      value: total > 0 ? (completed / total) * 100 : null,
      effectiveWeight: null,
    },
    {
      key: 'onSchedule',
      weight: 0.3,
      present: scheduled > 0,
      value: scheduled > 0 ? (upcoming / scheduled) * 100 : null,
      effectiveWeight: null,
    },
  ];

  var weightSum = 0;
  var weighted = 0;
  factors.forEach(function (f) {
    if (f.present) {
      weightSum += f.weight;
      weighted += f.weight * f.value;
    }
  });
  factors.forEach(function (f) {
    if (f.present && weightSum > 0) {
      f.effectiveWeight = f.weight / weightSum;
    }
  });

  var hasScore = weightSum > 0;
  var penalty = neglectAvailable
    ? Math.min(NEGLECT_PENALTY_CAP, NEGLECT_POINTS_PER_ASSET * neglectCount)
    : 0;
  var composite = hasScore ? Math.round(weighted / weightSum) : 0;
  // The penalty is always an integer, so subtracting it after rounding equals
  // rounding the difference -- and keeps the explainer math exact.
  var score = hasScore ? Math.max(0, composite - penalty) : 0;

  var gradeKey;
  var colorSuffix;
  if (score >= 75) {
    gradeKey = 'good';
    colorSuffix = 'good';
  } else if (score >= 50) {
    gradeKey = 'fair';
    colorSuffix = 'ok';
  } else {
    gradeKey = 'poor';
    colorSuffix = 'bad';
  }

  return {
    score: score,
    composite: composite,
    hasScore: hasScore,
    gradeKey: gradeKey,
    colorVar: 'var(--perf-health-' + colorSuffix + ')',
    arcDasharray: computeArcDasharray(score),
    factors: factors,
    neglect: {
      available: neglectAvailable,
      count: neglectCount,
      penalty: penalty,
    },
  };
}

// Module-level tracker of the last performance ref we rendered. Persists across
// mount/unmount so tab-switching / nav back (a remount with an unchanged
// performance ref) doesn't replay the entrance animation. Only the MOST RECENT
// ref is tracked, so any real counts change always animates.
var _lastSeenPerformance = null;

class InspectPerformance extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      animate: false,
      mounted: true,
      animKey: 0,
      lastPerformance: null,
      infoOpen: false,
      pendingSwitch: null,
    };
    this.handleInfoOpen = this.handleInfoOpen.bind(this);
    this.handleInfoClose = this.handleInfoClose.bind(this);
    this.handleInfoKeyDown = this.handleInfoKeyDown.bind(this);
    this.handleSwitchRequest = this.handleSwitchRequest.bind(this);
    this.handleSwitchConfirm = this.handleSwitchConfirm.bind(this);
    this.handleSwitchCancel = this.handleSwitchCancel.bind(this);
  }

  static getDerivedStateFromProps(nextProps, prevState) {
    if (prevState.lastPerformance === nextProps.performance) return null;
    var update = { lastPerformance: nextProps.performance };
    if (!nextProps.performance) return update;
    if (_lastSeenPerformance === nextProps.performance) {
      update.animate = false;
      update.mounted = true;
      return update;
    }
    _lastSeenPerformance = nextProps.performance;
    update.animate = true;
    update.mounted = false;
    // From the skeleton (prev performance null) keep animKey so the DOM stays
    // stable and CSS transitions run continuously. For a counts change (e.g.
    // switching inspections) bump animKey to remount and replay the entrance.
    var wasSkeleton = !prevState.lastPerformance;
    if (!wasSkeleton) {
      update.animKey = prevState.animKey + 1;
    }
    return update;
  }

  componentDidMount() {
    if (this.state.animate) this._scheduleMount();
    this._observeScrollWrap();
    this._observeHisto();
    this._measureLabels();
  }

  componentDidUpdate(prevProps, prevState) {
    if (
      prevState.animKey !== this.state.animKey ||
      (prevState.mounted && !this.state.mounted)
    ) {
      // The keyed inner container remounts on animKey change, replacing the
      // observed nodes; drop the stale refs so the observers re-attach.
      this._observedScrollWrap = null;
      this._observedHisto = null;
      this._scheduleMount();
    }
    this._observeScrollWrap();
    this._observeHisto();
    this._measureLabels();
  }

  componentWillUnmount() {
    if (this._scrollResizeObserver) {
      this._scrollResizeObserver.disconnect();
      this._scrollResizeObserver = null;
      this._observedScrollWrap = null;
    }
    if (this._histoResizeObserver) {
      this._histoResizeObserver.disconnect();
      this._histoResizeObserver = null;
      this._observedHisto = null;
    }
  }

  _scheduleMount() {
    var self = this;
    requestAnimationFrame(function () {
      requestAnimationFrame(function () {
        self.setState({ mounted: true });
      });
    });
  }

  // Equalize histogram label heights so any wrapped label grows the whole
  // row uniformly (keeps bars bottom-aligned and puts the baseline in the
  // right place via --perf-label-h, which the baseline CSS offset consumes).
  _measureLabels() {
    if (this._measurePending) return;
    this._measurePending = true;
    var self = this;
    requestAnimationFrame(function () {
      self._measurePending = false;
      var histo = document.querySelector('.perf-histo');
      if (!histo) return;
      var labels = histo.querySelectorAll('.perf-histo-label');
      if (!labels.length) return;
      for (var i = 0; i < labels.length; i++) {
        labels[i].style.minHeight = '';
      }
      var maxH = 0;
      for (var j = 0; j < labels.length; j++) {
        var h = labels[j].offsetHeight;
        if (h > maxH) maxH = h;
      }
      for (var k = 0; k < labels.length; k++) {
        labels[k].style.minHeight = maxH + 'px';
      }
      histo.style.setProperty('--perf-label-h', maxH + 'px');
    });
  }

  // Watch the histogram for size changes (caused by col-width changes or
  // container resize) and remeasure labels so wrapping updates stay uniform.
  _observeHisto() {
    if (typeof ResizeObserver === 'undefined') return;
    var histo = document.querySelector('.perf-histo');
    if (!histo) {
      if (this._observedHisto && this._histoResizeObserver) {
        this._histoResizeObserver.unobserve(this._observedHisto);
        this._observedHisto = null;
      }
      return;
    }
    if (histo === this._observedHisto) return;
    var self = this;
    if (!this._histoResizeObserver) {
      this._histoResizeObserver = new ResizeObserver(function () {
        self._measureLabels();
      });
    } else if (this._observedHisto) {
      this._histoResizeObserver.unobserve(this._observedHisto);
    }
    this._histoResizeObserver.observe(histo);
    this._observedHisto = histo;
  }

  // Size histogram columns against a minimum column width. If every bar fits
  // at that minimum, all fit naturally. If not, show N full + 1/3 peek where
  // N is the most that fits at the minimum. This lets density scale with
  // container size. Uses ResizeObserver on the scroll wrapper.
  _observeScrollWrap() {
    if (typeof ResizeObserver === 'undefined') return;
    var wrap = document.querySelector('.perf-histo-scroll-wrap');
    if (!wrap) {
      if (this._observedScrollWrap && this._scrollResizeObserver) {
        this._scrollResizeObserver.unobserve(this._observedScrollWrap);
        this._observedScrollWrap = null;
      }
      return;
    }
    if (wrap === this._observedScrollWrap) return;
    if (!this._scrollResizeObserver) {
      this._scrollResizeObserver = new ResizeObserver(function (entries) {
        for (var i = 0; i < entries.length; i++) {
          var el = entries[i].target;
          var W = entries[i].contentRect.width;
          var gap = 6;
          var minCol = 80;
          var count = parseInt(el.getAttribute('data-bar-count'), 10) || 1;
          var col;
          // Natural fit: all bars visible, no peek.
          var cNat = (W - (count - 1) * gap) / count;
          if (cNat >= minCol) {
            col = Math.floor(cNat);
          } else {
            // Peek mode: N full + 1/3 of next peeking.
            // N*col + N*gap + col/3 = W  =>  col = (W - N*gap) / (N + 1/3)
            // Solve for max N with col >= minCol:
            //   N <= (W - minCol/3) / (minCol + gap)
            var N = Math.floor((W - minCol / 3) / (minCol + gap));
            if (N < 1) N = 1;
            col = Math.floor((W - N * gap) / (N + 1 / 3));
          }
          el.style.setProperty('--perf-col-width', col + 'px');
        }
      });
    } else if (this._observedScrollWrap) {
      this._scrollResizeObserver.unobserve(this._observedScrollWrap);
    }
    this._scrollResizeObserver.observe(wrap);
    this._observedScrollWrap = wrap;
  }

  // Translate with a fallback when no key is registered (t returns the key).
  _t(key, fallback) {
    var t = this.props.t;
    if (!t) return fallback;
    var translated = t(key);
    return translated === key ? fallback : translated;
  }

  handleInfoOpen() {
    this.setState({ infoOpen: true });
  }

  // The health card is a role="button" div (its footer chips are real
  // <button>s, and buttons cannot nest), so Enter/Space activation is wired
  // up manually to match native button behavior.
  handleInfoKeyDown(e) {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      this.setState({ infoOpen: true });
    }
  }

  handleInfoClose() {
    this.setState({ infoOpen: false });
  }

  // A histogram bar click doesn't switch the inspection right away -- it
  // stages the target uuid/name so the confirmation dialog can name it.
  handleSwitchRequest(entry) {
    this.setState({
      pendingSwitch: { uuid: entry.inspectionUUID, name: entry.name },
    });
  }

  handleSwitchConfirm() {
    var pendingSwitch = this.state.pendingSwitch;
    if (pendingSwitch && this.props.onInspectionClick) {
      this.props.onInspectionClick(pendingSwitch.uuid);
    }
    this.setState({ pendingSwitch: null });
  }

  handleSwitchCancel() {
    this.setState({ pendingSwitch: null });
  }

  // Count chips for whatever is costing health points: Open Deficiencies
  // (the failed count) and Overdue (red) and Neglected (purple), each
  // navigating like its KPI card. Zero and unavailable counts render nothing.
  renderFactorChips(view) {
    var performance = this.props.performance;
    var onCardClick = this.props.onCardClick;
    var overdue = (performance && performance.overdue) || 0;
    var chips = [];
    var failed = (performance && performance.failed) || 0;
    if (failed > 0) {
      // Chips sit inside the clickable health card; stop propagation so a
      // chip click navigates without also opening the explainer dialog.
      var clickOpen = function (e) {
        e.stopPropagation();
        if (onCardClick) onCardClick('failed');
      };
      chips.push(
        <button
          key="open-deficiencies"
          type="button"
          className="perf-health-factor bad"
          onClick={clickOpen}
        >
          {this._t(
            'inspect:perfChipOpenDeficiencies',
            '{n} Open Deficiencies',
          ).replace('{n}', failed)}
        </button>,
      );
    }
    if (overdue > 0) {
      var clickOverdue = function (e) {
        e.stopPropagation();
        if (onCardClick) onCardClick('overdue');
      };
      chips.push(
        <button
          key="overdue"
          type="button"
          className="perf-health-factor bad"
          onClick={clickOverdue}
        >
          {this._t('inspect:perfChipOverdue', '{n} Overdue').replace(
            '{n}',
            overdue,
          )}
        </button>,
      );
    }
    if (view.neglect.available && view.neglect.count > 0) {
      var clickNeglected = function (e) {
        e.stopPropagation();
        if (onCardClick) onCardClick('neglected');
      };
      chips.push(
        <button
          key="neglected"
          type="button"
          className="perf-health-factor neglect"
          onClick={clickNeglected}
        >
          {this._t('inspect:perfChipNeglected', '{n} Neglected').replace(
            '{n}',
            view.neglect.count,
          )}
        </button>,
      );
    }
    return chips;
  }

  renderHealthCard(view, delay) {
    var colorVar = view.colorVar;
    var gradeFallback =
      view.gradeKey === 'good'
        ? 'Good'
        : view.gradeKey === 'fair'
          ? 'Fair'
          : 'Poor';
    var gradeLabel = this._t(
      'inspect:perfGrade_' + view.gradeKey,
      gradeFallback,
    );
    var passFactor = view.factors[0];
    return (
      <div
        className="perf-card perf-health"
        style={{ '--d': delay + 'ms' }}
        role="button"
        tabIndex={0}
        aria-haspopup="dialog"
        aria-label={this._t(
          'inspect:perfInfoTitle',
          'How Health Score Is Calculated',
        )}
        onClick={this.handleInfoOpen}
        onKeyDown={this.handleInfoKeyDown}
      >
        <div className="perf-card-label" style={{ alignSelf: 'flex-start' }}>
          <span className="perf-card-text">
            {this._t('inspect:perfHealthTitle', 'Health Score')}
          </span>
        </div>
        <div className="perf-health-arc">
          <svg width="120" height="120" viewBox="0 0 120 120">
            <circle
              cx="60"
              cy="60"
              r="48"
              className="perf-arc-trk"
              strokeWidth="9"
              strokeDasharray="226.2 75.4"
              transform="rotate(135 60 60)"
            />
            <circle
              cx="60"
              cy="60"
              r="48"
              className="perf-arc-fl"
              stroke={colorVar}
              strokeWidth="9"
              style={{ '--arc-target': view.arcDasharray }}
              transform="rotate(135 60 60)"
            />
          </svg>
          <div className="perf-health-arc-center">
            <div className="perf-health-arc-score" style={{ color: colorVar }}>
              {view.score}
            </div>
            <div className="perf-health-arc-grade" style={{ color: colorVar }}>
              {gradeLabel}
            </div>
          </div>
        </div>
        <div className="perf-health-footer">
          {passFactor.present && (
            <div className="perf-health-pass">
              <span
                className="perf-health-pass-val"
                style={{ color: colorVar }}
              >
                {Math.round(passFactor.value)}%
              </span>
              <span className="perf-health-pass-lbl">
                {this._t('inspect:perfPassRate', 'Pass Rate')}
              </span>
            </div>
          )}
          <div className="perf-health-factors">
            {this.renderFactorChips(view)}
          </div>
        </div>
      </div>
    );
  }

  renderHealthCardSkeleton(delay) {
    var nbsp = ' ';
    return (
      <div className="perf-card perf-health" style={{ '--d': delay + 'ms' }}>
        <div className="perf-card-label" style={{ alignSelf: 'flex-start' }}>
          <span className="perf-card-text">{nbsp}</span>
        </div>
        <div className="perf-health-arc">
          <svg width="120" height="120" viewBox="0 0 120 120">
            <circle
              cx="60"
              cy="60"
              r="48"
              className="perf-arc-trk"
              strokeWidth="9"
              strokeDasharray="226.2 75.4"
              transform="rotate(135 60 60)"
            />
            <circle
              cx="60"
              cy="60"
              r="48"
              className="perf-arc-fl"
              strokeWidth="9"
              transform="rotate(135 60 60)"
            />
          </svg>
          <div className="perf-health-arc-center">
            <div className="perf-health-arc-score">{nbsp}</div>
            <div className="perf-health-arc-grade">{nbsp}</div>
          </div>
        </div>
        <div className="perf-health-footer">
          <div className="perf-health-pass">
            <span className="perf-health-pass-val">{nbsp}</span>
            <span className="perf-health-pass-lbl">{nbsp}</span>
          </div>
        </div>
      </div>
    );
  }

  // No-history variant of the health card. Inspections exist and the tab has
  // forward-looking data (overdue / upcoming), but no submissions have been
  // graded yet, so there is no pass rate to plot. Rather than a blank gauge
  // (which reads as a perpetual loader or an ambiguous 0%), show a calm
  // "awaiting data" state: a gauge-dial glyph -- a miniature of the gauge that
  // will appear -- above a short explanation.
  renderHealthCardEmpty(delay) {
    return (
      <div
        className="perf-card perf-health perf-health-empty"
        style={{ '--d': delay + 'ms' }}
      >
        <div className="perf-card-label" style={{ alignSelf: 'flex-start' }}>
          <span className="perf-card-text">
            {this._t('inspect:perfHealthTitle', 'Health Score')}
          </span>
        </div>
        <div className="perf-health-empty-body">
          <div className="perf-health-empty-badge">
            <span className="material-symbols-outlined">speed</span>
          </div>
          <div className="perf-health-empty-title">
            {this._t('inspect:perfHealthEmptyTitle', 'No Score Yet')}
          </div>
          <div className="perf-health-empty-sub">
            {this._t(
              'inspect:perfHealthEmptySub',
              'Pass rate appears once an inspection is submitted.',
            )}
          </div>
        </div>
      </div>
    );
  }

  renderKpiCardSkeleton(delay) {
    var nbsp = ' ';
    return (
      <div className="perf-card perf-kpi" style={{ '--d': delay + 'ms' }}>
        <div className="perf-card-label">
          <span className="perf-card-text">{nbsp}</span>
        </div>
        <div className="perf-card-value">
          <span className="perf-card-text">{nbsp}</span>
        </div>
        <div className="perf-card-sub">
          <span className="perf-card-text">{nbsp}</span>
        </div>
      </div>
    );
  }

  // The seven overview skeleton cards (health + 6 KPI). Shared by the
  // no-inspections CTA branch and the loading branch so the grid always holds
  // seven cards in every pre-data state, satisfying the CSS :nth-child rules.
  renderOverviewSkeletonCards() {
    return (
      <React.Fragment>
        {this.renderHealthCardSkeleton(50)}
        {this.renderKpiCardSkeleton(80)}
        {this.renderKpiCardSkeleton(120)}
        {this.renderKpiCardSkeleton(160)}
        {this.renderKpiCardSkeleton(200)}
        {this.renderKpiCardSkeleton(240)}
        {this.renderKpiCardSkeleton(280)}
      </React.Fragment>
    );
  }

  // Real, data-backed KPI card. onClick navigates with a matching filter
  // preset: Total / Passed / Failed go to the History tab, Overdue / Upcoming /
  // Neglected to the Scheduled tab. Falls back to noop when no handler is
  // supplied. colorVar null leaves the value at the default color.
  renderKpiCard(label, value, subtitle, colorVar, delay, onClick) {
    var valueStyle = colorVar ? { color: colorVar } : {};
    // Dim zero counts (no semantic color of their own) so the meaningful
    // non-zero values read as the signal. A value with a colorVar is non-zero
    // by construction, so it keeps its color.
    var isZero = value === 0 || value === '0';
    var valueClass =
      'perf-card-value' + (!colorVar && isZero ? ' perf-value-muted' : '');
    return (
      <button
        type="button"
        className="perf-card perf-kpi"
        style={{ '--d': delay + 'ms' }}
        onClick={onClick || noop}
      >
        <div className="perf-card-label">
          <span className="perf-card-text">{label}</span>
        </div>
        <div className={valueClass} style={valueStyle}>
          <span className="perf-card-text">{value}</span>
        </div>
        <div className="perf-card-sub">
          <span className="perf-card-text">{subtitle}</span>
        </div>
      </button>
    );
  }

  // No-value KPI placeholder: same card, inert, value shown as an em dash.
  // Used for the schedule-derived metrics the endpoint does not deliver yet.
  renderKpiPlaceholder(label, delay) {
    return (
      <div className="perf-card perf-kpi" style={{ '--d': delay + 'ms' }}>
        <div className="perf-card-label">
          <span className="perf-card-text">{label}</span>
        </div>
        <div className="perf-card-value perf-value-muted">
          <span className="perf-card-text">{'—'}</span>
        </div>
        <div className="perf-card-sub">
          <span className="perf-card-text"> </span>
        </div>
      </div>
    );
  }

  // Inert placeholder column: hidden pct/label, zero-height grey bar. Used
  // both by the all-placeholder histogram (endpoint without the breakdown)
  // and to pad a 2-3 bar half-width histogram out to four slots.
  renderEmptyColumn(key) {
    return (
      <div key={key} className="perf-histo-col empty">
        <div className="perf-histo-bar-wrap">
          <div className="perf-histo-bar" style={{ '--h': '0' }}>
            <div className="perf-histo-pct">-</div>
          </div>
        </div>
        <div className="perf-histo-label">-</div>
      </div>
    );
  }

  // The "Pass Rate by Inspection" section rendered with no values: four empty
  // placeholder columns. Shown only when the endpoint does not deliver the
  // passRateByInspection breakdown (older API).
  renderEmptyHistogram(baseDelay) {
    var bars = [];
    for (var i = 0; i < 4; i++) {
      bars.push(this.renderEmptyColumn('empty-' + i));
    }
    return (
      <div className="perf-grid" style={{ gridTemplateColumns: '1fr' }}>
        <div
          className="perf-card perf-card-histo perf-card-half"
          style={{ '--d': baseDelay + 'ms' }}
        >
          <div className="perf-histo">
            <div className="perf-histo-baseline" />
            {bars}
          </div>
        </div>
      </div>
    );
  }

  // Real per-inspection histogram, ported from the demo-branch implementation
  // and adapted to the API data shape. Each column is one inspection; passRate
  // is a 0-1 fraction or null (no graded submissions in range -> labeled
  // column with a muted dash and no bar). 2-4 inspections render the
  // half-width card padded with empty slots to four columns; 5+ switch to the
  // full-width scrolling variant whose column width _observeScrollWrap sizes
  // from the container. Clicking a column narrows the Inspect selection to
  // that inspection via onInspectionClick.
  renderHistogram(rates, baseDelay) {
    var self = this;
    var onInspectionClick = this.props.onInspectionClick;
    var count = rates.length;
    var isHalf = count <= 4;
    // Cap the stagger so the entrance doesn't take forever with many bars.
    var staggerMs = count > 16 ? 20 : count > 8 ? 40 : 60;
    var cols = rates.map(function (entry, i) {
      var barDelay = baseDelay + 160 + i * staggerMs;
      var pctDelay = barDelay + 300;
      var hasRate = entry.passRate !== null && entry.passRate !== undefined;
      var pct = hasRate ? Math.round(entry.passRate * 100) : null;
      var color = hasRate ? getBarColor(pct) : null;
      var handleClick = function () {
        if (onInspectionClick) self.handleSwitchRequest(entry);
      };
      var barStyle = hasRate
        ? {
            '--h': Math.max(pct, 6) + '%',
            '--bar-d': barDelay + 'ms',
            background: color,
          }
        : { '--h': '0' };
      var pctStyle = hasRate
        ? { color: color, '--pct-d': pctDelay + 'ms' }
        : { '--pct-d': pctDelay + 'ms' };
      return (
        <button
          key={entry.inspectionUUID}
          type="button"
          className={'perf-histo-col' + (hasRate ? '' : ' no-data')}
          onClick={handleClick}
        >
          <div className="perf-histo-bar-wrap">
            <div className="perf-histo-bar" style={barStyle}>
              <div className="perf-histo-pct" style={pctStyle}>
                {hasRate ? pct + '%' : '—'}
              </div>
            </div>
          </div>
          <div className="perf-histo-label">{entry.name}</div>
        </button>
      );
    });
    if (isHalf) {
      for (var i = cols.length; i < 4; i++) {
        cols.push(this.renderEmptyColumn('empty-' + i));
      }
    }
    var histoInner = (
      <div className={'perf-histo' + (isHalf ? '' : ' perf-histo-scroll')}>
        <div className="perf-histo-baseline" />
        {cols}
      </div>
    );
    return (
      <div className="perf-grid" style={{ gridTemplateColumns: '1fr' }}>
        <div
          className={
            'perf-card perf-card-histo' +
            (isHalf ? ' perf-card-half' : ' perf-card-histo-scroll')
          }
          style={{ '--d': baseDelay + 'ms' }}
        >
          {isHalf ? (
            histoInner
          ) : (
            <div className="perf-histo-scroll-wrap" data-bar-count={count}>
              {histoInner}
            </div>
          )}
        </div>
      </div>
    );
  }

  render() {
    var performance = this.props.performance;
    var mountedClass = this.state.mounted ? ' perf-mounted' : '';
    var animateClass = this.state.animate ? ' perf-animate' : '';

    // Branch 1: no inspections configured -- skeleton-framed CTA.
    if (this.props.noInspections) {
      return (
        <div
          className={'perf-content perf-empty' + animateClass + mountedClass}
        >
          <div
            key={'pc-inner-' + this.state.animKey}
            className="perf-content-inner"
          >
            <div className="perf-section-label" style={{ '--d': '0ms' }}>
              {this._t('inspect:perfOverview', 'Overview')}
            </div>
            <div className="perf-overview-grid">
              {this.renderOverviewSkeletonCards()}
            </div>
            <div className="perf-empty-cta-wrap">
              <div className="perf-empty-cta" style={{ '--d': '200ms' }}>
                <EmptyState
                  icon="fact_check"
                  title={this.props.emptyTitle}
                  description={this.props.emptyDescription}
                  buttonLabel={this.props.emptyActionLabel}
                  onAction={this.props.onCreateInspection}
                />
              </div>
            </div>
          </div>
        </div>
      );
    }

    // Branch 2: no performance loaded yet (loading or pre-fetch) -- skeleton.
    if (!performance) {
      return (
        <div
          className={'perf-content perf-skeleton' + animateClass + mountedClass}
        >
          <div
            key={'pc-inner-' + this.state.animKey}
            className="perf-content-inner"
          >
            <div className="perf-section-label" style={{ '--d': '0ms' }}>
              {this._t('inspect:perfOverview', 'Overview')}
            </div>
            <div className="perf-overview-grid">
              {this.renderOverviewSkeletonCards()}
            </div>
          </div>
        </div>
      );
    }

    // Branch 3: loaded but the payload has no data in ANY dimension -- empty
    // state. `total` is a History count (passed+failed+incomplete), while
    // `overdue`/`upcoming`/`neglected` are Scheduled counts delivered in the
    // same /performance response. Show the empty state only when all four are
    // zero; otherwise fall through to the Overview so non-zero schedule metrics
    // (e.g. upcoming with no past submissions, or neglected as the only signal)
    // are not hidden. The `> 0` checks are null-safe for null/undefined fields
    // (null > 0 and undefined > 0 are both false).
    var hasPerformanceData =
      performance.total > 0 ||
      performance.overdue > 0 ||
      performance.upcoming > 0 ||
      performance.neglected > 0;
    if (!hasPerformanceData) {
      return (
        <div className="inspect-tab-empty">
          <EmptyState
            icon="monitoring"
            title={this.props.noDataTitle}
            description={this.props.noDataDescription}
          />
        </div>
      );
    }

    // Branch 4: loaded with data -- the real Overview.
    var view = computeHealthView(performance);
    var goodColor = 'var(--perf-health-good)';
    var badColor = 'var(--perf-health-bad)';
    // With no history submissions (total=0) the 'of N total' subtitle is not
    // meaningful (it would read 'of 0 total') -- blank it instead, matching the
    // blank subtitle of the other cards.
    var hasHistory = performance.total > 0;
    // "No Score Yet" appears when no ratio factor has data -- e.g. a payload
    // whose only signal is a neglected count reaches the Overview (branch 3
    // lets it through) but yields no score to plot.
    var healthCard = view.hasScore
      ? this.renderHealthCard(view, 50)
      : this.renderHealthCardEmpty(50);
    var ofTotal = hasHistory
      ? this._t('inspect:perfSubOfTotal', 'of {n} total').replace(
          '{n}',
          performance.total,
        )
      : ' ';
    var onCardClick = this.props.onCardClick;
    var clickTotal = onCardClick
      ? function () {
          onCardClick('total');
        }
      : noop;
    var clickPassed = onCardClick
      ? function () {
          onCardClick('passed');
        }
      : noop;
    var clickFailed = onCardClick
      ? function () {
          onCardClick('failed');
        }
      : noop;
    var clickOverdue = onCardClick
      ? function () {
          onCardClick('overdue');
        }
      : noop;
    var clickUpcoming = onCardClick
      ? function () {
          onCardClick('upcoming');
        }
      : noop;
    var clickNeglected = onCardClick
      ? function () {
          onCardClick('neglected');
        }
      : noop;
    // `neglected` is nullable in the store: null/undefined means the endpoint
    // does not deliver the count yet, so keep the inert em-dash placeholder;
    // a real number (including 0) renders the live, clickable card.
    var neglectedCard =
      performance.neglected !== null && performance.neglected !== undefined
        ? this.renderKpiCard(
            this._t('inspect:perfKpiNeglected', 'Neglected'),
            performance.neglected,
            ' ',
            performance.neglected > 0 ? 'var(--perf-health-neglected)' : null,
            280,
            clickNeglected,
          )
        : this.renderKpiPlaceholder(
            this._t('inspect:perfKpiNeglected', 'Neglected'),
            280,
          );

    // Pass Rate by Inspection: null means the endpoint does not deliver the
    // breakdown (older API) -- keep the inert placeholder. A delivered list
    // of 0 or 1 entries means the data represents a single inspection whose
    // aggregate pass rate is already on the health card -- hide the section.
    var rates = performance.passRateByInspection;
    var histogramSection = null;
    if (rates === null || rates === undefined) {
      histogramSection = this.renderEmptyHistogram(360);
    } else if (rates.length > 1) {
      histogramSection = this.renderHistogram(rates, 360);
    }
    var pendingName = this.state.pendingSwitch
      ? this.state.pendingSwitch.name
      : '';

    return (
      <div className={'perf-content' + animateClass + mountedClass}>
        <div
          key={'pc-inner-' + this.state.animKey}
          className="perf-content-inner"
        >
          <div className="perf-section-label" style={{ '--d': '0ms' }}>
            {this._t('inspect:perfOverview', 'Overview')}
          </div>
          <div className="perf-overview-grid">
            {healthCard}
            {this.renderKpiCard(
              this._t('inspect:perfKpiTotal', 'Total'),
              performance.total,
              ' ',
              null,
              80,
              clickTotal,
            )}
            {this.renderKpiCard(
              this._t('inspect:perfKpiPassed', 'Passed'),
              performance.passed,
              performance.corrected > 0
                ? this._t('inspect:perfSubCorrected', '{n} corrected').replace(
                    '{n}',
                    performance.corrected,
                  )
                : ofTotal,
              performance.passed > 0 ? goodColor : null,
              120,
              clickPassed,
            )}
            {this.renderKpiCard(
              this._t('inspect:perfKpiOpenDeficiencies', 'Open Deficiencies'),
              performance.failed,
              ofTotal,
              performance.failed > 0 ? badColor : null,
              160,
              clickFailed,
            )}
            {this.renderKpiCard(
              this._t('inspect:perfKpiOverdue', 'Overdue'),
              performance.overdue,
              ' ',
              performance.overdue > 0 ? badColor : null,
              200,
              clickOverdue,
            )}
            {this.renderKpiCard(
              this._t('inspect:perfKpiUpcoming', 'Upcoming'),
              performance.upcoming,
              ' ',
              null,
              240,
              clickUpcoming,
            )}
            {neglectedCard}
          </div>

          {histogramSection && (
            <React.Fragment>
              <div className="perf-section-label" style={{ '--d': '320ms' }}>
                {this._t(
                  'inspect:perfSectionInspection',
                  'Pass Rate by Inspection',
                )}
              </div>
              {histogramSection}
            </React.Fragment>
          )}
          <InspectHealthInfoDialog
            open={this.state.infoOpen}
            onClose={this.handleInfoClose}
            view={view}
            t={this.props.t}
          />
          <SlateDialog
            open={!!this.state.pendingSwitch}
            onClose={this.handleSwitchCancel}
            title={this._t('inspect:perfSwitchTitle', 'Switch Inspection')}
            maxWidth="sm"
            actions={[
              {
                label: this._t('btn:cancel', 'Cancel'),
                onClick: this.handleSwitchCancel,
                variant: 'secondary',
                key: 'cancel',
              },
              {
                label: this._t('inspect:perfSwitchConfirm', 'Switch'),
                onClick: this.handleSwitchConfirm,
                variant: 'primary',
                key: 'switch',
              },
            ]}
          >
            <div className="perf-info-content">
              <div className="perf-info-intro">
                {this._t(
                  'inspect:perfSwitchMsg',
                  'Switch Inspect to "{name}"? All tabs will show only this inspection.',
                ).replace('{name}', pendingName)}
              </div>
            </div>
          </SlateDialog>
        </div>
      </div>
    );
  }
}

InspectPerformance.propTypes = {
  performance: PropTypes.object,
  noInspections: PropTypes.bool,
  onCardClick: PropTypes.func,
  onInspectionClick: PropTypes.func,
  onCreateInspection: PropTypes.func,
  emptyTitle: PropTypes.string,
  emptyDescription: PropTypes.string,
  emptyActionLabel: PropTypes.string,
  noDataTitle: PropTypes.string,
  noDataDescription: PropTypes.string,
  t: PropTypes.func,
};

export default InspectPerformance;
